import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Lock, ArrowRight, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ResetPasswordPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password !== confirmPassword) {
      toast.error('Las contraseñas no coinciden');
      return;
    }
    if (password.length < 6) {
      toast.error('La contraseña debe tener al menos 6 caracteres');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token, new_password: password });
      setSuccess(true);
      toast.success('Contraseña actualizada');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al cambiar la contraseña');
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4" style={{ background: 'linear-gradient(135deg, #F8FAF6 0%, #EDF2E8 50%, #F0F4EC 100%)' }}>
        <div className="bg-white rounded-2xl shadow-xl p-7 max-w-sm w-full text-center">
          <p className="text-gray-500 mb-4">Enlace inválido. Solicita un nuevo enlace de recuperación.</p>
          <Button onClick={() => navigate('/forgot-password')} style={{ background: '#467322' }} className="text-white">
            Solicitar nuevo enlace
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{ background: 'linear-gradient(135deg, #F8FAF6 0%, #EDF2E8 50%, #F0F4EC 100%)' }}>
      <div className="w-full max-w-sm">
        <div className="text-center mb-8">
          <img src="https://customer-assets.emergentagent.com/job_nutrifit-portal/artifacts/s4srivcq_Logo%20Icono%20Solo%20Nutriquimia%20SVG.svg" alt="Nutriquimia" className="h-14 mx-auto mb-3" />
          <h1 className="text-xl font-bold text-gray-900" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>Nutriquimia</h1>
        </div>

        <div className="bg-white rounded-2xl shadow-xl shadow-black/[0.06] p-7">
          {!success ? (
            <>
              <h2 className="text-xl font-bold text-gray-900 text-center mb-0.5" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                Nueva Contraseña
              </h2>
              <p className="text-xs text-gray-400 text-center mb-6">
                Ingresa tu nueva contraseña
              </p>

              <form onSubmit={handleSubmit} className="space-y-4" autoComplete="off">
                <div>
                  <Label className="text-[11px] text-gray-400 font-semibold mb-1 block">Nueva Contraseña</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={15} />
                    <Input
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="Mínimo 6 caracteres"
                      required
                      autoComplete="new-password"
                      className="h-11 pl-9 bg-gray-50/80 border border-gray-200/60 focus:border-[#467322] focus:ring-1 focus:ring-[#467322]/15 rounded-xl text-sm"
                      data-testid="new-password-input"
                    />
                  </div>
                </div>

                <div>
                  <Label className="text-[11px] text-gray-400 font-semibold mb-1 block">Confirmar Contraseña</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={15} />
                    <Input
                      type="password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      placeholder="Repite tu contraseña"
                      required
                      autoComplete="new-password"
                      className="h-11 pl-9 bg-gray-50/80 border border-gray-200/60 focus:border-[#467322] focus:ring-1 focus:ring-[#467322]/15 rounded-xl text-sm"
                      data-testid="confirm-password-input"
                    />
                  </div>
                </div>

                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full h-11 text-white text-sm font-bold rounded-xl transition-all duration-300 hover:-translate-y-0.5 group"
                  style={{ background: '#467322' }}
                  data-testid="reset-confirm-btn"
                >
                  {loading ? 'Guardando...' : 'Cambiar Contraseña'}
                  {!loading && <ArrowRight size={15} className="ml-1.5 group-hover:translate-x-1 transition-transform" />}
                </Button>
              </form>
            </>
          ) : (
            <div className="text-center py-4" data-testid="reset-success-message">
              <div className="w-16 h-16 bg-gradient-to-br from-emerald-50 to-green-50 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <CheckCircle size={28} className="text-emerald-500" />
              </div>
              <h2 className="text-xl font-bold text-gray-900 mb-2" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                Contraseña Actualizada
              </h2>
              <p className="text-sm text-gray-500 mb-5">
                Tu contraseña ha sido cambiada exitosamente
              </p>
              <Button
                onClick={() => navigate('/login')}
                className="w-full h-11 text-white text-sm font-bold rounded-xl"
                style={{ background: '#467322' }}
                data-testid="go-to-login-btn"
              >
                Iniciar Sesión
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
